import React from 'react';
import { Link, useParams } from 'react-router-dom';
import { ArrowRight, Phone, MapPin, CheckCircle, KeyRound, Wrench, Shield, Key } from 'lucide-react';
import { Button } from './ui/button';
import { Card, CardContent } from './ui/card';
import { businessInfo } from '../mockData';
import { siteConfig, residentialCards, servicePages, fixedQuoteLine } from '../content/siteContent';
import { serviceAreas } from '../content/serviceAreas';
import usePageMeta from '../hooks/usePageMeta';
import NotFound from '../pages/NotFound';

const iconMap = { Wrench, Shield, KeyRound, Key };
const bySlug = Object.fromEntries(serviceAreas.map((a) => [a.slug, a]));

const ServiceAreaPage = () => {
  const { slug } = useParams();
  const area = bySlug[slug];
  usePageMeta();
  if (!area) return <NotFound />;

  const nearby = (area.nearby || []).map((s) => bySlug[s]).filter(Boolean);

  return (
    <div className="min-h-screen" key={slug}>
      {/* Hero */}
      <section className="relative bg-gray-950 py-16 sm:py-20 text-white overflow-hidden">
        <div className="absolute inset-0" aria-hidden="true">
          <img src="/images/stock/residential-banner-red-door.jpg" alt="" className="w-full h-full object-cover" />
          <div className="absolute inset-0 bg-gradient-to-r from-gray-950 from-25% via-gray-950/75 via-55% to-gray-950/5"></div>
        </div>
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-3xl">
            <div className="inline-flex items-center gap-2 bg-white/10 px-4 py-2 rounded-full text-sm font-medium mb-5">
              <MapPin size={16} /> North Shore, Auckland
            </div>
            <h1 className="text-4xl sm:text-5xl font-bold mb-5">Locksmith in {area.name}</h1>
            <p className="text-xl text-gray-300 mb-8">
              Mobile locksmith service for {area.name} — we come to you for lockouts, rekeying,
              key cutting, lock repairs and home security.
            </p>
            <div className="flex flex-col sm:flex-row gap-4">
              <a href={`tel:${businessInfo.phoneTel}`}>
                <Button size="lg" className="bg-red-600 text-white hover:bg-red-700 font-bold text-lg px-8">
                  <Phone className="mr-2" size={20} />
                  Call {businessInfo.phone}
                </Button>
              </a>
              <Link to="/contact">
                <Button size="lg" variant="outline" className="border-white text-white hover:bg-white/10 font-semibold text-lg px-8">
                  Get a quote
                </Button>
              </Link>
            </div>
            <p className="text-sm text-gray-300 mt-5">{siteConfig.hoursLine} · {fixedQuoteLine}</p>
          </div>
        </div>
      </section>

      {/* Local intro */}
      <section className="py-14 sm:py-16 bg-white">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-2xl sm:text-3xl font-bold text-gray-900 mb-5">
            Your local {area.name} locksmith
          </h2>
          <p className="text-lg text-gray-700 leading-relaxed mb-6">{area.blurb}</p>
          <ul className="grid sm:grid-cols-2 gap-3">
            {[
              'Mobile — we come to you',
              'Fixed quote before any work starts',
              '12-month workmanship guarantee',
              'The install team the pros trust',
            ].map((t) => (
              <li key={t} className="flex items-start gap-2 text-gray-700">
                <CheckCircle className="text-red-600 flex-shrink-0 mt-1" size={18} />
                <span>{t}</span>
              </li>
            ))}
          </ul>
        </div>
      </section>

      {/* Services */}
      <section className="py-14 sm:py-16 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-2xl sm:text-3xl font-bold text-gray-900 text-center mb-10">
            Locksmith services in {area.name}
          </h2>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-5">
            {residentialCards.map((c) => {
              const cfg = servicePages.residential[c.slug];
              const Icon = iconMap[c.icon] || Key;
              return (
                <Link key={c.slug} to={`/residential/${c.slug}`} className="block group">
                  <Card className="border-gray-200 h-full bg-white group-hover:border-red-300 group-hover:shadow-lg transition-all duration-300">
                    <CardContent className="p-6 flex flex-col h-full">
                      <div className="w-12 h-12 bg-red-50 rounded-lg flex items-center justify-center mb-4">
                        <Icon className="text-red-600" size={24} />
                      </div>
                      <h3 className="text-lg font-bold text-gray-900 mb-2">{cfg.title}</h3>
                      <p className="text-sm text-gray-600 flex-1">{c.blurb}</p>
                      <span className="text-red-600 font-semibold text-sm flex items-center gap-1 mt-4">
                        Learn more <ArrowRight size={14} />
                      </span>
                    </CardContent>
                  </Card>
                </Link>
              );
            })}
          </div>
          <p className="text-center text-gray-600 mt-8">
            Business or building in {area.name}?{' '}
            <Link to="/commercial" className="text-red-600 font-semibold">See our commercial services →</Link>
          </p>
        </div>
      </section>

      {/* Nearby areas — internal linking */}
      {nearby.length > 0 && (
        <section className="py-14 bg-white">
          <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
            <h2 className="text-2xl font-bold text-gray-900 mb-6">We also cover nearby</h2>
            <div className="flex flex-wrap justify-center gap-3">
              {nearby.map((n) => (
                <Link
                  key={n.slug}
                  to={`/service-areas/${n.slug}`}
                  className="inline-flex items-center gap-1.5 text-red-600 font-semibold border border-red-200 rounded-full px-5 py-2 hover:bg-red-50 transition-colors"
                >
                  <MapPin size={14} /> {n.name}
                </Link>
              ))}
              <Link
                to="/service-areas"
                className="inline-flex items-center gap-1.5 text-gray-700 font-semibold border border-gray-200 rounded-full px-5 py-2 hover:bg-gray-50 transition-colors"
              >
                All service areas <ArrowRight size={14} />
              </Link>
            </div>
          </div>
        </section>
      )}

      {/* CTA */}
      <section className="py-14 bg-gray-950">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl font-bold text-white mb-3">Need a locksmith in {area.name}?</h2>
          <p className="text-lg text-gray-300 mb-7">We'll get to you as fast as we can. {fixedQuoteLine}</p>
          <a href={`tel:${businessInfo.phoneTel}`}>
            <Button size="lg" className="bg-red-600 text-white hover:bg-red-700 font-bold">
              <Phone className="mr-2" size={20} />
              Call {businessInfo.phone}
            </Button>
          </a>
        </div>
      </section>
    </div>
  );
};

export default ServiceAreaPage;
